/* eslint-disable prettier/prettier */
import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  CaretSortIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from 'app/components/ui/button';

import { Input } from 'app/components/ui/input';
import { Toaster } from 'app/components/ui/toaster';
import { Pencil, SearchIcon, Trash, Plus, Tag } from 'lucide-react';
import { Badge } from 'app/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/hooks/debounce-hook';
import { AlertProductTagsDelete } from '../delete/delete-confirmation';
import DialogAddProductTag from '../Add';
import DialogUpdateProductTags from '../Edit';
import { useProductsSlice } from '../../slice';

export function DataTable() {
  const [openUpdate, setOpenUpdate] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedRow, setSelectedRow] = React.useState<any>(null);

  const { useLazyGetProductsTagsQuery } = useProductsSlice();
  const [getTags, { data, error, isLoading }] = useLazyGetProductsTagsQuery();

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'tag_id',
      header: 'ID',
      cell: ({ row }) => (
        <div className="font-mono text-xs text-gray-500 dark:text-gray-400 text-center">
          #{row.getValue('tag_id')}
        </div>
      ),
    },
    {
      accessorKey: 'tag_name',
      header: 'Tag Name',
      cell: ({ row }) => (
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-gradient-to-r from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
            <Tag className="w-4 h-4 text-green-600 dark:text-green-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-gray-100 truncate">
            {row.getValue('tag_name')}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center justify-center space-x-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedRow(row.original);
              setOpenUpdate(true);
            }}
            className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700"
          >
            <Pencil className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setSelectedRow(row.original);
              setOpenDelete(true);
            }}
            className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
          >
            <Trash className="w-4 h-4 text-red-600 dark:text-red-400" />
          </Button>
        </div>
      ),
    },
  ];

  const labelsPagination = {
    totalPages: data?.response?.totalPages,
    pageSize: data?.response?.perPage,
  };
  const labels = data?.response?.data || [];
  const dispatch = useDispatch();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [selected, setSelected] = React.useState<number>(0);
  const [rowSelected, setRowSelected] = React.useState<any>([]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: labels,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: labelsPagination?.totalPages,
    rowCount: labelsPagination?.pageSize,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  useEffect(() => {
    getTags({
      page: pagination.pageIndex + 1,
      search: searchDebounce,
    });
  }, [pagination, searchDebounce]);

  return (
    <div className="p-6">
      {/* Search and Add Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="relative flex-1 max-w-sm">
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search tags..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-10 h-10 bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-green-500 focus:border-green-500 rounded-lg transition-all duration-200"
          />
        </div>
        <Button
          onClick={() => setOpenAdd(true)}
          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white transition-all duration-200 h-10 px-6"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Tag
        </Button>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <Table>
          <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
            {table.getHeaderGroups().map(headerGroup => (
              <TableRow key={headerGroup.id} className="border-gray-200 dark:border-gray-700">
                {headerGroup.headers.map((header, index) => (
                  <TableHead
                    key={header.id}
                    className={`text-gray-700 dark:text-gray-300 font-semibold ${index === 0 ? 'text-center w-20' :
                        index === 1 ? 'text-left w-auto' :
                          'text-center w-32'
                      }`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors duration-200"
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      className={`py-4 ${index === 0 ? 'text-center' :
                          index === 1 ? 'text-left' :
                            'text-center'
                        }`}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-32 text-center text-gray-500 dark:text-gray-400"
                >
                  <div className="flex flex-col items-center space-y-2">
                    <Tag className="w-8 h-8 text-gray-300 dark:text-gray-600" />
                    <span>{isLoading ? 'Loading...' : 'No tags found'}</span>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing page {pagination.pageIndex + 1} of {table.getPageCount()}
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.firstPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-8 w-8 p-0"
          >
            {'<<'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="h-8 px-3"
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="h-8 px-3"
          >
            Next
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => table.lastPage()}
            disabled={!table.getCanNextPage()}
            className="h-8 w-8 p-0"
          >
            {'>>'}
          </Button>
        </div>
      </div>

      <Toaster />

      <DialogUpdateProductTags
        isOpen={openUpdate}
        onOpenChange={setOpenUpdate}
        data={selectedRow}
      />

      <DialogAddProductTag isOpen={openAdd} onClose={setOpenAdd} />

      <AlertProductTagsDelete
        isOpen={openDelete}
        onOpenChange={setOpenDelete}
        data={selectedRow}
      />
    </div>
  );
}